/**
 * ATT Stringing Configurator — cart contract v2
 * ---------------------------------------------------------------------------
 * Replaces ShopPad Infinite Options on racket product pages.
 *
 * WHY THE SPEC LIVES ON THE SERVICE LINE (changed in v2)
 *
 * A native Shopify bundle is one line in the cart and several lines on the
 * order: the parent is replaced by its components. Order #199168 proved that
 * every property written to the parent line is discarded in that swap — both
 * component lines arrived with no attributes at all, so a bundle bought with
 * "Choose Your Own" would have recorded neither string nor tension.
 *
 * The stringing service line is an ordinary product that never expands, so its
 * properties always survive. It is also the line that *is* the job. So from v2
 * the racket line carries only the pairing token, and the service line carries
 * the whole spec.
 *
 * That change also makes multiple rackets per line natural: a bundle holding
 * two frames gets either one service line covering both, or one service line
 * per frame when the specs differ.
 *
 * Do not change property keys without bumping SPEC_VERSION, updating the
 * validation function's accepted versions, and telling YesSort.
 */
(function () {
  'use strict';

  var SPEC_VERSION = '2';

  var root = document.getElementById('stringing-configurator');
  if (!root) return;

  var AFTER_ADD = root.dataset.afterAdd === 'stay' ? 'stay' : 'cart';

  // ---------------------------------------------------------------------------
  // Config
  // ---------------------------------------------------------------------------

  function readJSON(selector, fallback) {
    var el = root.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[stringing] could not parse config', selector, e);
      return fallback;
    }
  }

  function bySortOrder(a, b) {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return (a.label || '').localeCompare(b.label || '');
  }

  var tiers = readJSON('[data-stringing-tiers]', []).sort(bySortOrder);
  var strings = readJSON('[data-stringing-strings]', []).sort(bySortOrder);

  /**
   * variantId -> variant title, emitted by Liquid. Replaces the DOM scraping
   * that produced "Head Extreme MP Auxetic — Grip Size:" with no grip on every
   * order placed before this fix.
   */
  var variantTitles = readJSON('[data-stringing-variants]', {});

  if (!tiers.length) return;

  var tensionMin = parseInt(root.dataset.tensionMin, 10);
  var tensionMax = parseInt(root.dataset.tensionMax, 10);
  var recommendedString = root.dataset.recommendedString || '';

  /** How many rackets one unit of this product contains. 2 for the Defyer bundle. */
  var racketsPerUnit = Math.max(1, parseInt(root.dataset.racketsPerUnit, 10) || 1);
  var isMulti = racketsPerUnit > 1;

  // ---------------------------------------------------------------------------
  // Elements
  // ---------------------------------------------------------------------------

  var el = {
    toggle: root.querySelector('.stringing__toggle'),
    panel: root.querySelector('.stringing__panel'),
    summary: root.querySelector('[data-stringing-summary]'),
    tierList: root.querySelector('[data-stringing-tier-list]'),
    sameField: root.querySelector('[data-stringing-same-field]'),
    same: root.querySelector('[data-stringing-same]'),
    sameLabel: root.querySelector('[data-stringing-same-label]'),
    specs: root.querySelector('[data-stringing-specs]'),
    error: root.querySelector('[data-stringing-error]'),
    price: root.querySelector('[data-stringing-price]'),
    priceRacket: root.querySelector('[data-stringing-price-racket]'),
    priceService: root.querySelector('[data-stringing-price-service]'),
    priceTotal: root.querySelector('[data-stringing-price-total]')
  };

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  var state = {
    tier: null,
    sameForAll: true,
    /** One entry per rendered spec group. Length is 1 when sameForAll. */
    specs: []
  };

  function blankSpec() {
    return {
      split: false,
      string: '',
      mains: '',
      crosses: '',
      att: false,
      tension: String(root.dataset.tensionDefault || ''),
      mainsTension: String(root.dataset.tensionDefault || ''),
      crossesTension: String(root.dataset.tensionDefault || '')
    };
  }

  function groupCount() {
    return state.sameForAll ? 1 : racketsPerUnit;
  }

  // ---------------------------------------------------------------------------
  // Tier list
  // ---------------------------------------------------------------------------

  function racketShipsUnstrung() {
    return (root.dataset.racketStrung || '').toLowerCase() === 'unstrung';
  }

  function racketShipsStrung() {
    return (root.dataset.racketStrung || '').toLowerCase() === 'strung';
  }

  function noStringingDescription() {
    if (racketShipsUnstrung()) {
      return isMulti ? 'Frames only — no strings fitted.' : 'Frame only — no strings fitted.';
    }
    if (racketShipsStrung()) return "Keep the manufacturer's factory strings.";
    return 'Leave as supplied.';
  }

  function collapsedHint() {
    if (racketShipsUnstrung()) return isMulti ? 'Frames only — add stringing below' : 'Frame only — add stringing below';
    if (racketShipsStrung()) return 'Factory strings — or upgrade below';
    return 'Choose a stringing service';
  }

  function renderTier(handle, label, description, price) {
    var id = 'stringing-tier-' + (handle || 'none');
    var node = document.createElement('label');
    node.className = 'stringing__tier';
    node.setAttribute('for', id);
    node.innerHTML =
      '<input type="radio" name="stringing-tier" id="' + id + '" value="' + handle + '"' +
      (handle === '' ? ' checked' : '') + '>' +
      '<span class="stringing__tier-body">' +
      '<span class="stringing__tier-label">' + escapeHTML(label) + '</span>' +
      (description ? '<span class="stringing__tier-desc">' + escapeHTML(description) + '</span>' : '') +
      '</span>' +
      (price ? '<span class="stringing__tier-price">' + escapeHTML(price) + '</span>' : '');
    el.tierList.appendChild(node);
  }

  renderTier('', 'No stringing', noStringingDescription(), '');
  tiers.forEach(function (tier) {
    // On a multi-racket line the fee is charged per racket, so say so up front
    // rather than letting the total surprise them at the summary.
    var price = tier.priceFormatted;
    if (isMulti && tier.price > 0) price = price + ' each';
    renderTier(tier.handle, tier.label, tier.description, price);
  });

  // ---------------------------------------------------------------------------
  // Spec groups
  // ---------------------------------------------------------------------------

  function stringsForTier(tier) {
    return strings.filter(function (s) {
      if (!s.tiers) return true;
      return s.tiers.split(',').indexOf(tier.handle) !== -1;
    });
  }

  function optionsHTML(list, placeholder, selected) {
    var html = '<option value="">' + escapeHTML(placeholder) + '</option>';
    list.forEach(function (s) {
      var label = s.label + (s.construction ? ' — ' + s.construction : '');
      html += '<option value="' + escapeHTML(s.handle) + '"' +
        (s.handle === selected ? ' selected' : '') + '>' + escapeHTML(label) + '</option>';
    });
    return html;
  }

  function renderSpecs() {
    el.specs.innerHTML = '';
    var tier = state.tier;
    if (!tier) return;

    var count = groupCount();
    while (state.specs.length < count) state.specs.push(blankSpec());
    state.specs.length = count;

    var available = stringsForTier(tier);

    for (var i = 0; i < count; i++) {
      el.specs.appendChild(specGroup(i, tier, available));
    }
  }

  function specGroup(index, tier, available) {
    var spec = state.specs[index];
    var wrap = document.createElement('div');
    wrap.className = 'stringing__group';
    wrap.dataset.specIndex = String(index);

    var needsHeading = !state.sameForAll && racketsPerUnit > 1;
    if (needsHeading) {
      var h = document.createElement('p');
      h.className = 'stringing__group-heading';
      h.textContent = 'Racket ' + (index + 1) + ' of ' + racketsPerUnit;
      wrap.appendChild(h);
    }

    if (tier.allowsSplitBed) {
      wrap.appendChild(
        field('', checkbox('split', spec.split, 'Different strings or tensions in the mains and crosses'))
      );
    }

    if (tier.requiresString) {
      if (spec.split) {
        wrap.appendChild(field('Mains', select('mains', available, 'Choose the mains string', spec.mains)));
        if (tier.requiresTension && !spec.att) {
          wrap.appendChild(field('', number('mainsTension', 'Mains tension (lb)', spec.mainsTension)));
        }
        wrap.appendChild(field('Crosses', select('crosses', available, 'Choose the crosses string', spec.crosses)));
        if (tier.requiresTension && !spec.att) {
          wrap.appendChild(field('', number('crossesTension', 'Crosses tension (lb)', spec.crossesTension)));
        }
      } else {
        wrap.appendChild(field('String', select('string', available, 'Choose a string', spec.string)));
      }
    }

    if (tier.requiresTension) {
      var tensionBits = document.createDocumentFragment();
      if (tier.allowsAttChoice) {
        tensionBits.appendChild(checkbox('att', spec.att, 'Let ATT choose the tension for me'));
      }
      if (!spec.att && !spec.split) {
        tensionBits.appendChild(
          number('tension', 'Tension (lb) — ' + tensionMin + ' to ' + tensionMax, spec.tension)
        );
      }
      wrap.appendChild(field('Tension', tensionBits));
    }

    return wrap;
  }

  function field(legend, content) {
    var fs = document.createElement('fieldset');
    fs.className = 'stringing__field';
    if (legend) {
      var lg = document.createElement('legend');
      lg.className = 'stringing__legend';
      lg.textContent = legend;
      fs.appendChild(lg);
    }
    fs.appendChild(content);
    return fs;
  }

  function select(key, list, placeholder, selected) {
    var s = document.createElement('select');
    s.className = 'stringing__select';
    s.dataset.specKey = key;
    s.setAttribute('aria-label', placeholder);
    s.innerHTML = optionsHTML(list, placeholder, selected);
    return s;
  }

  function number(key, label, value) {
    var wrap = document.createElement('div');
    wrap.className = 'stringing__tension';
    var id = 'stringing-' + key + '-' + Math.random().toString(36).slice(2, 7);
    var lab = document.createElement('label');
    lab.className = 'stringing__label';
    lab.setAttribute('for', id);
    lab.textContent = label;
    var input = document.createElement('input');
    input.className = 'stringing__number';
    input.type = 'number';
    input.id = id;
    input.dataset.specKey = key;
    input.min = String(tensionMin);
    input.max = String(tensionMax);
    input.step = '1';
    input.value = value;
    wrap.appendChild(lab);
    wrap.appendChild(input);
    return wrap;
  }

  function checkbox(key, checked, label) {
    var lab = document.createElement('label');
    lab.className = 'stringing__checkbox';
    var input = document.createElement('input');
    input.type = 'checkbox';
    input.dataset.specKey = key;
    input.checked = !!checked;
    var span = document.createElement('span');
    span.textContent = label;
    lab.appendChild(input);
    lab.appendChild(span);
    return lab;
  }

  // Spec edits are delegated, because the groups are re-rendered constantly.
  el.specs.addEventListener('change', function (event) {
    var target = event.target;
    var key = target.dataset && target.dataset.specKey;
    if (!key) return;

    var group = target.closest('[data-spec-index]');
    if (!group) return;
    var spec = state.specs[Number(group.dataset.specIndex)];
    if (!spec) return;

    spec[key] = target.type === 'checkbox' ? target.checked : target.value;

    showError(null);
    // Toggling split or the ATT choice changes which controls exist.
    if (key === 'split' || key === 'att') {
      renderSpecs();
    }
    updateSummary();
    updatePrice();
  });

  // ---------------------------------------------------------------------------
  // Visibility
  // ---------------------------------------------------------------------------

  function refresh() {
    var tier = state.tier;

    show(el.sameField, !!tier && isMulti);
    if (el.sameLabel) {
      el.sameLabel.textContent = 'String all ' + racketsPerUnit + ' rackets the same way';
    }

    if (!tier) state.specs = [];

    renderSpecs();
    updateSummary();
    updatePrice();
    publishState();
  }

  function show(node, visible) {
    if (!node) return;
    node.hidden = !visible;
  }

  // ---------------------------------------------------------------------------
  // Summary and price
  // ---------------------------------------------------------------------------

  function stringByHandle(handle) {
    if (!handle) return null;
    for (var i = 0; i < strings.length; i++) {
      if (strings[i].handle === handle) return strings[i];
    }
    return null;
  }

  function describeSpec(spec) {
    if (!state.tier) return '';
    if (spec.split) {
      var m = stringByHandle(spec.mains);
      var c = stringByHandle(spec.crosses);
      if (m || c) return (m ? m.label : '…') + ' / ' + (c ? c.label : '…');
      return '';
    }
    var s = stringByHandle(spec.string);
    if (s) return s.label;
    if (!state.tier.requiresString && recommendedString) return recommendedString;
    return '';
  }

  function updateSummary() {
    if (!el.summary) return;
    if (!state.tier) {
      el.summary.textContent = collapsedHint();
      return;
    }
    var parts = [state.tier.label];
    var described = state.specs.map(describeSpec).filter(Boolean);
    if (described.length) {
      parts.push(state.sameForAll ? described[0] : described.join(' / '));
    }
    if (isMulti) parts.push('×' + racketsPerUnit);
    el.summary.textContent = parts.join(' · ');
  }

  function racketPriceCents() {
    var input = variantInput();
    if (input && window.__stringingVariantPrices && window.__stringingVariantPrices[input.value]) {
      return window.__stringingVariantPrices[input.value];
    }
    var priceEl = document.querySelector('.product-info .price-list .price, .price-list .price');
    if (priceEl) {
      var digits = priceEl.textContent.replace(/[^0-9.,]/g, '').replace(/,/g, '');
      var value = parseFloat(digits);
      if (!isNaN(value)) return Math.round(value * 100);
    }
    return null;
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '£' + (cents / 100).toFixed(2);
  }

  function updatePrice() {
    if (!el.price) return;
    if (!state.tier) {
      el.price.hidden = true;
      return;
    }
    var racket = racketPriceCents();
    var service = parseInt(state.tier.price, 10) * racketsPerUnit;
    if (racket === null || isNaN(service)) {
      el.price.hidden = true;
      return;
    }
    el.priceRacket.textContent = formatMoney(racket);
    el.priceService.textContent =
      formatMoney(service) + ' stringing' + (isMulti ? ' (' + racketsPerUnit + ' rackets)' : '');
    el.priceTotal.textContent = 'Total ' + formatMoney(racket + service);
    el.price.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Delivery calculator bridge
  // ---------------------------------------------------------------------------

  function publishState() {
    var leadDays = state.tier ? (parseInt(state.tier.leadDays, 10) || 0) : 0;

    root.dataset.stringingSelected = state.tier ? 'true' : 'false';
    root.dataset.stringingTier = state.tier ? state.tier.handle : '';
    root.dataset.stringingLeadDays = String(leadDays);

    window.stringingExtraHandling = leadDays;
    window.infiniteOptionsExtraHandling = leadDays;

    document.dispatchEvent(new CustomEvent('stringing:change', {
      detail: { selected: !!state.tier, tier: state.tier ? state.tier.handle : null, leadDays: leadDays }
    }));

    if (window.DeliveryCalculator && typeof window.DeliveryCalculator.calculateDelivery === 'function') {
      setTimeout(function () { window.DeliveryCalculator.calculateDelivery(); }, 50);
    }

    suppressAcceleratedCheckout(!!state.tier);
  }

  /**
   * Shop Pay and friends submit straight to checkout, skipping the cart and the
   * paired add below — the exact route to a racket with stringing attached and
   * nothing paid. Hidden whenever a tier is selected.
   */
  function suppressAcceleratedCheckout(selected) {
    document.querySelectorAll('.shopify-payment-button').forEach(function (node) {
      node.style.display = selected ? 'none' : '';
    });
  }

  // ---------------------------------------------------------------------------
  // Validation — mirrors the server-side function so errors arrive early
  // ---------------------------------------------------------------------------

  function validTension(value) {
    var n = parseInt(value, 10);
    return !isNaN(n) && n >= tensionMin && n <= tensionMax;
  }

  function tensionMessage(bed, index) {
    var who = state.sameForAll || racketsPerUnit === 1 ? '' : 'racket ' + (index + 1) + "'s ";
    return 'Enter ' + (who ? 'a ' + who : 'a ') + (bed ? bed + ' ' : '') +
      'tension between ' + tensionMin + ' and ' + tensionMax + ' lb.';
  }

  function validate() {
    var tier = state.tier;
    if (!tier) return null;

    for (var i = 0; i < state.specs.length; i++) {
      var spec = state.specs[i];
      var who = state.sameForAll || racketsPerUnit === 1 ? '' : ' for racket ' + (i + 1);

      if (tier.requiresString) {
        if (spec.split) {
          if (!spec.mains) return 'Choose a string for the mains' + who + '.';
          if (!spec.crosses) return 'Choose a string for the crosses' + who + '.';
        } else if (!spec.string) {
          return 'Choose a string' + who + '.';
        }
      }

      if (tier.requiresTension && !spec.att) {
        if (spec.split) {
          if (!validTension(spec.mainsTension)) return tensionMessage('mains', i);
          if (!validTension(spec.crossesTension)) return tensionMessage('crosses', i);
        } else if (!validTension(spec.tension)) {
          return tensionMessage(null, i);
        }
      }
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Cart contract
  // ---------------------------------------------------------------------------

  function racketLabel() {
    var title = root.dataset.productTitle || '';
    var input = variantInput();
    var variantTitle = input ? variantTitles[String(input.value)] : '';
    if (variantTitle && variantTitle !== 'Default Title') {
      title += ' — ' + variantTitle;
    }
    return title;
  }

  /**
   * Properties for one stringing service line.
   *
   * `_racket_units` is how many rackets this line covers per unit of the racket
   * line, so the cart script and the validation function can both check
   * quantities without re-deriving anything.
   */
  function serviceProperties(spec, ref, units, index) {
    var tier = state.tier;
    var props = {
      _stringing_ref: ref,
      _stringing_tier: tier.handle,
      _split_bed: spec.split ? 'true' : 'false',
      _racket_units: String(units),
      _spec_version: SPEC_VERSION,
      Racket: racketLabel(),
      Stringing: tier.label
    };

    if (index !== null) {
      props['_racket_index'] = String(index + 1);
      props['For'] = 'Racket ' + (index + 1) + ' of ' + racketsPerUnit;
    }

    if (spec.split) {
      var mains = stringByHandle(spec.mains);
      var crosses = stringByHandle(spec.crosses);
      props['Mains'] = mains.label;
      props['Crosses'] = crosses.label;
      props._mains_handle = mains.handle;
      props._crosses_handle = crosses.handle;
      if (mains.reelVariantId) props._mains_variant = String(mains.reelVariantId);
      if (crosses.reelVariantId) props._crosses_variant = String(crosses.reelVariantId);

      if (spec.att) {
        props['Mains tension'] = 'ATT Recommended';
        props['Crosses tension'] = 'ATT Recommended';
        props._mains_tension = 'att';
        props._crosses_tension = 'att';
      } else {
        props['Mains tension'] = spec.mainsTension + ' lb';
        props['Crosses tension'] = spec.crossesTension + ' lb';
        props._mains_tension = String(spec.mainsTension);
        props._crosses_tension = String(spec.crossesTension);
      }
      return props;
    }

    var chosen = stringByHandle(spec.string);
    if (chosen) {
      props['String'] = chosen.label;
      props._string_handle = chosen.handle;
      if (chosen.reelVariantId) props._string_variant = String(chosen.reelVariantId);
    } else if (recommendedString) {
      props['String'] = recommendedString + ' (ATT recommended)';
    }

    if (tier.requiresTension) {
      if (spec.att) {
        props['Tension'] = 'ATT Recommended';
        props._tension = 'att';
      } else {
        props['Tension'] = spec.tension + ' lb';
        props._tension = String(spec.tension);
      }
    }

    return props;
  }

  /**
   * The racket line carries the pairing token and nothing else. Everything it
   * might carry is lost when a bundle expands into its components.
   */
  function racketProperties(ref) {
    return {
      _stringing_ref: ref,
      _spec_version: SPEC_VERSION,
      _rackets_per_unit: String(racketsPerUnit)
    };
  }

  // ---------------------------------------------------------------------------
  // Add to cart
  // ---------------------------------------------------------------------------

  function productForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].querySelector('input[name="id"], select[name="id"]')) return forms[i];
    }
    return null;
  }

  function variantInput() {
    var form = productForm();
    if (!form) return null;
    return form.querySelector('input[name="id"], select[name="id"]');
  }

  function quantity() {
    var form = productForm();
    if (!form) return 1;
    var input = form.querySelector('input[name="quantity"]');
    var n = input ? parseInt(input.value, 10) : 1;
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function makeRef() {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID().split('-')[0] + Date.now().toString(36).slice(-4);
    }
    return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
  }

  function showError(message) {
    if (!el.error) return;
    el.error.textContent = message || '';
    el.error.hidden = !message;
    if (message) openPanel();
  }

  function buildItems(ref, qty, variantId) {
    var items = [{ id: Number(variantId), quantity: qty, properties: racketProperties(ref) }];
    var serviceVariant = Number(state.tier.variantId);

    if (state.sameForAll) {
      // One line covering every racket on this line.
      items.push({
        id: serviceVariant,
        quantity: qty * racketsPerUnit,
        properties: serviceProperties(state.specs[0], ref, racketsPerUnit, null)
      });
    } else {
      // One line per racket. Differing properties keep them separate; identical
      // ones would merge, which is harmless because the spec is then the same.
      state.specs.forEach(function (spec, index) {
        items.push({
          id: serviceVariant,
          quantity: qty,
          properties: serviceProperties(spec, ref, 1, index)
        });
      });
    }

    return items;
  }

  function addPaired() {
    var problem = validate();
    if (problem) {
      showError(problem);
      return Promise.reject(new Error(problem));
    }
    showError(null);

    var input = variantInput();
    if (!input) return Promise.reject(new Error('No variant input on the page'));

    var payload = { items: buildItems(makeRef(), quantity(), input.value) };

    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (body) {
            throw new Error(body.description || 'Could not add to cart');
          });
        }
        return response.json();
      })
      .then(function (result) {
        if (AFTER_ADD === 'cart') {
          window.location.href = (window.Shopify.routes.root || '/') + 'cart';
          return result;
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('variant:add', { bubbles: true }));
        return result;
      })
      .catch(function (error) {
        showError(error.message);
        throw error;
      });
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
      if (!state.tier) return;

      event.preventDefault();
      event.stopPropagation();

      var buttons = form.querySelectorAll('[type="submit"]');
      buttons.forEach(function (b) { b.setAttribute('aria-busy', 'true'); b.disabled = true; });

      addPaired().finally(function () {
        buttons.forEach(function (b) { b.removeAttribute('aria-busy'); b.disabled = false; });
      });
    },
    true
  );

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  function openPanel() {
    el.panel.hidden = false;
    el.toggle.setAttribute('aria-expanded', 'true');
  }

  el.toggle.addEventListener('click', function () {
    var open = el.toggle.getAttribute('aria-expanded') === 'true';
    el.panel.hidden = open;
    el.toggle.setAttribute('aria-expanded', String(!open));
  });

  el.tierList.addEventListener('change', function (event) {
    var handle = event.target.value;
    state.tier = tiers.filter(function (t) { return t.handle === handle; })[0] || null;

    /**
     * Who picks the tension follows the tier: on ATT Recommended the customer is
     * already delegating the string choice, so ATT chooses the tension too — but
     * the control stays visible and unticking it reveals a tension input.
     */
    if (state.tier) {
      var attDefault = !state.tier.requiresString;
      state.specs = state.specs.map(function (spec) {
        spec.att = attDefault;
        return spec;
      });
      if (!state.specs.length) {
        var first = blankSpec();
        first.att = attDefault;
        state.specs = [first];
      }
    }

    showError(null);
    refresh();
  });

  if (el.same) {
    el.same.addEventListener('change', function () {
      state.sameForAll = el.same.checked;
      // Seed each racket from the first spec so switching to per-racket editing
      // starts from what they already chose rather than an empty form.
      if (!state.sameForAll && state.specs.length) {
        var seed = state.specs[0];
        state.specs = [];
        for (var i = 0; i < racketsPerUnit; i++) {
          state.specs.push(Object.assign({}, seed));
        }
      }
      showError(null);
      refresh();
    });
  }

  document.addEventListener('variant:change', function () {
    setTimeout(updatePrice, 50);
  });

  function escapeHTML(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : value;
    return div.innerHTML;
  }

  refresh();
})();